import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useLeafletContext } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet.vectorgrid';

/**
 * React-Leaflet wrapper for Leaflet.VectorGrid.Protobuf.
 * Renders Mapbox Vector Tiles (.pbf) from our PostGIS tile endpoint.
 *
 * Props:
 *  - url:          MVT tile URL template with {z}/{x}/{y} placeholders
 *  - layerName:    Name of the MVT layer inside the tile (e.g. 'parcels')
 *  - vectorTileLayerStyles: Style config keyed by layer name
 *  - interactive:  Whether features are interactive (default true)
 *  - maxZoom:      Maximum zoom level (default 18)
 *  - minZoom:      Minimum zoom level (default 0)
 *  - onClick:      Callback when a feature is clicked: (properties, latlng) => void
 *  - getFeatureId: Function to extract a unique ID from feature properties
 */
const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

function VectorTileLayer({
  url,
  layerName = 'parcels',
  vectorTileLayerStyles,
  interactive = true,
  maxZoom = 18,
  minZoom = 0,
  onClick,
  getFeatureId,
}) {
  const map = useMap();
  // Get the leaflet container we should add to. Inside a
  // <LayersControl.Overlay>, the context's layerContainer is the
  // overlay's LayerGroup — adding to it lets the LayersControl
  // checkbox actually toggle this layer. Outside an Overlay, the
  // layerContainer is the map itself, so the layer is always-on.
  // The previous version called `vtLayer.addTo(map)` unconditionally,
  // which bypassed the checkbox machinery entirely — the parcel
  // boundaries vector tiles were always-fetching even when the
  // "Parcel Boundaries (Vector Tiles)" overlay was unchecked.
  const ctx = useLeafletContext();
  const layerRef = useRef(null);

  // `vectorTileLayerStyles`, `onClick`, and `getFeatureId` are
  // typically inline functions / fresh object literals that change
  // identity on every parent render. If we put them in the effect's
  // dep array, every MapView re-render (viewport change, filter
  // update, state flip) would tear down the vector tile layer and
  // re-fetch every tile from our MVT endpoint — expensive and
  // disruptive. Capture them in refs so the effect reads the latest
  // values without depending on them.
  const stylesRef = useRef(vectorTileLayerStyles);
  const onClickRef = useRef(onClick);
  const getFeatureIdRef = useRef(getFeatureId);
  useEffect(() => { stylesRef.current = vectorTileLayerStyles; }, [vectorTileLayerStyles]);
  useEffect(() => { onClickRef.current = onClick; }, [onClick]);
  useEffect(() => { getFeatureIdRef.current = getFeatureId; }, [getFeatureId]);

  useEffect(() => {
    if (!url) return;

    // Build full URL if relative
    const tileUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

    const defaultStyles = {
      [layerName]: (properties) => {
        const isListed = properties.is_listed;
        const hasBoundary = properties.has_boundary;

        // Points (centroids at low zoom) vs polygons (at high zoom)
        if (!hasBoundary || map.getZoom() < 10) {
          return {
            weight: 0,
            fillColor: isListed ? '#FFD700' : '#3498db',
            fillOpacity: 0.7,
            fill: true,
            radius: 5,
          };
        }

        // #1807: the tile carries `outline_oversize` when the polygon is at
        // least twice the parcel's stated acreage, i.e. probably the larger
        // parent's outline and not this parcel's boundary. Draw it the way
        // the boundaries overlay draws an approximate outline (dashed, lighter
        // fill) so it is not read as the boundary. Absent on tiles cached
        // before the flag existed, which then draw as before.
        if (properties.outline_oversize) {
          return {
            weight: isListed ? 2 : 1,
            color: isListed ? '#FFD700' : '#3498db',
            fillColor: isListed ? '#FFD700' : '#3498db',
            fillOpacity: 0.03,
            fill: true,
            dashArray: '6,4',
          };
        }

        return {
          weight: isListed ? 2.5 : 1.5,
          color: isListed ? '#FFD700' : '#3498db',
          fillColor: isListed ? '#FFD700' : '#3498db',
          fillOpacity: isListed ? 0.15 : 0.08,
          fill: true,
        };
      },
    };

    const styles = stylesRef.current || defaultStyles;

    const options = {
      vectorTileLayerStyles: styles,
      interactive,
      maxZoom,
      minZoom,
      rendererFactory: L.canvas.tile,
      maxNativeZoom: 16,
    };

    if (getFeatureIdRef.current) {
      options.getFeatureId = getFeatureIdRef.current;
    }

    const vtLayer = L.vectorGrid.protobuf(tileUrl, options);

    if (interactive) {
      vtLayer.on('click', (e) => {
        // Read onClick through the ref so parents passing a fresh
        // arrow function on every render still get the latest
        // handler without forcing the layer to be rebuilt.
        if (onClickRef.current && e.latlng) {
          const props = e.layer?.properties;
          onClickRef.current(props, e.latlng);
        }
      });
    }

    // Add to the layerContainer from leaflet context (the overlay's
    // LayerGroup when wrapped in <LayersControl.Overlay>, otherwise
    // the map). LayersControl tracks layers added to its child
    // LayerGroups and toggles them via its checkbox UI.
    const container = ctx.layerContainer || map;
    container.addLayer(vtLayer);
    layerRef.current = vtLayer;

    return () => {
      if (layerRef.current) {
        container.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, ctx, url, layerName, interactive, maxZoom, minZoom]);

  return null;
}

export default VectorTileLayer;
